import { Banner, Button, Icon, Modal, ModalBody } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import {
  MouseEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useKey } from 'react-use';

import { rewindConfig } from 'components/rewind';
import { createAppModal } from 'utils/modal';

import {
  AnimatedBackground,
  ClosingSlide,
  ImpactSlide,
  JourneySlide,
  OpeningSlide,
  RewindControls,
  SlideProgressBar,
  StyleSlide,
  WinsSlide,
} from './components';
import {
  RewindProvider,
  useRewindCapture,
  useRewindData,
  useRewindMusic,
  useRewindSlides,
} from './hooks';
import styles from './rewindModal.module.scss';
import { REWIND_SLIDES } from './rewindModal.types';

export const RewindModal = createAppModal(
  { type: 'app:rewind', featureFlag: 'dixa-rewind' },
  ({ isOpen, hide, modal: { arg } }): JSX.Element => {
    const { rewindData, isLoading, isError, fetchRewindData } = useRewindData();

    const {
      isMuted,
      playMusic,
      pauseMusic,
      resumeMusic,
      stopMusic,
      fadeMusic,
      seekToTime,
      toggleMute,
    } = useRewindMusic();

    const {
      currentSlideIndex,
      currentSlideId,
      progress,
      isPaused,
      isFirstSlide,
      isLastSlide,
      completedSlides,
      goToSlide,
      goToNextSlide,
      goToPreviousSlide,
      getTimelinePositionForSlide,
      markSlideComplete,
      togglePause,
      setMousePaused,
    } = useRewindSlides();

    // Defer progress value to avoid blocking main thread during animations
    const deferredProgress = useDeferredValue(progress);

    const [isHovered, setIsHovered] = useState(false);
    const holdPauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const { captureAndSave, captureAndCopyToClipboard, isCopying, isSaving } =
      useRewindCapture();

    // Mouse hold pause - only triggers after holding for 150ms to avoid quick clicks
    const handleMouseDown = useCallback(() => {
      holdPauseTimeoutRef.current = setTimeout(() => {
        setMousePaused(true);
      }, 150);
    }, [setMousePaused]);

    const handleMouseUp = useCallback(() => {
      if (holdPauseTimeoutRef.current) {
        clearTimeout(holdPauseTimeoutRef.current);
        holdPauseTimeoutRef.current = null;
      }
      setMousePaused(false);
    }, [setMousePaused]);

    // Load data when modal opens (during opening slide)
    useEffect(() => {
      if (!isOpen || !arg.userId) return;
      fetchRewindData(arg.userId);
    }, [arg.userId, fetchRewindData, isOpen]);

    // Start background music when modal opens
    useEffect(() => {
      if (isOpen) {
        playMusic();
      } else {
        stopMusic();
      }
    }, [isOpen, playMusic, stopMusic]);

    // Sync music pause state with slides pause state
    useEffect(() => {
      if (isPaused) {
        pauseMusic();
      } else {
        resumeMusic();
      }
    }, [isPaused, pauseMusic, resumeMusic]);

    // Stop progress & music on error
    useEffect(() => {
      if (isError) {
        togglePause();
      }
    }, [isError, togglePause]);

    // Handle slide click with audio sync
    const handleSlideClick = useCallback(
      (index: number) => {
        if (isError) return;

        goToSlide(index);

        // Seek audio to the start of that slide
        const timelinePosition = getTimelinePositionForSlide(index);
        seekToTime(timelinePosition);

        ampli.rewindSlideViewed({
          year: rewindConfig.year,
          slide: REWIND_SLIDES[index],
        });
      },
      [isError, goToSlide, getTimelinePositionForSlide, seekToTime],
    );

    // Handle next slide with audio sync
    const handleNextSlide = useCallback(() => {
      if (isError) return;

      if (!isLastSlide) {
        goToNextSlide();
        const timelinePosition = getTimelinePositionForSlide(
          currentSlideIndex + 1,
        );
        seekToTime(timelinePosition);
      }
    }, [
      isError,
      isLastSlide,
      goToNextSlide,
      getTimelinePositionForSlide,
      currentSlideIndex,
      seekToTime,
    ]);

    // Handle previous slide with audio sync
    const handlePreviousSlide = useCallback(() => {
      if (isError) return;

      if (!isFirstSlide) {
        goToPreviousSlide();
        const timelinePosition = getTimelinePositionForSlide(
          currentSlideIndex - 1,
        );
        seekToTime(timelinePosition);
      }
    }, [
      isError,
      isFirstSlide,
      goToPreviousSlide,
      getTimelinePositionForSlide,
      currentSlideIndex,
      seekToTime,
    ]);

    // Keyboard navigation with music sync
    useKey(
      'ArrowRight',
      (e) => {
        e.preventDefault();
        handleNextSlide();
      },
      { event: 'keydown' },
      [handleNextSlide],
    );

    useKey(
      'ArrowLeft',
      (e) => {
        e.preventDefault();
        handlePreviousSlide();
      },
      { event: 'keydown' },
      [handlePreviousSlide],
    );

    // Click navigation (left/right sides of the modal)
    const handleContainerClick = (event: MouseEvent<HTMLDivElement>): void => {
      if (isError) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const containerWidth = rect.width;

      // Click on left 30% goes back, right 30% goes forward
      if (clickX < containerWidth * 0.3) {
        const prevIndex = Math.max(0, currentSlideIndex - 1);
        handleSlideClick(prevIndex);
      } else if (clickX > containerWidth * 0.7) {
        const nextIndex = Math.min(5, currentSlideIndex + 1); // 6 slides total (0-5)
        handleSlideClick(nextIndex);
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={hide}
        className={styles.modal}
        allowClickOutside
        allowEscClose
      >
        <ModalBody className={styles.modalBody}>
          <RewindProvider
            data={rewindData}
            isLoading={isLoading}
            isError={isError}
            userId={arg.userId}
          >
            <div
              ref={slideContainerRef}
              className={styles.rewindContainer}
              onClick={handleContainerClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => {
                setIsHovered(false);
                handleMouseUp();
              }}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
            >
              <AnimatedBackground currentSlideIndex={currentSlideIndex} />

              {!isError && (
                <SlideProgressBar
                  currentSlideIndex={currentSlideIndex}
                  progress={deferredProgress}
                  onSlideClick={handleSlideClick}
                />
              )}

              {/* Watermark banner - visible on middle slides only when capturing screenshot */}
              {(isSaving || isCopying) &&
                currentSlideId !== 'opening' &&
                currentSlideId !== 'closing' && (
                  <div className={styles.watermark} data-capture-watermark>
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className={styles.watermarkItem}>
                        <Icon name="dixa" size={16} />
                        <span className={styles.watermarkText}>
                          Dixa Rewind {rewindConfig.year}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

              <div className={styles.slidesContainer}>
                {currentSlideId === 'opening' && (
                  <OpeningSlide isCompleted={completedSlides.has('opening')} />
                )}

                {!isError && rewindData && (
                  <>
                    {currentSlideId === 'journey' && (
                      <JourneySlide
                        isCompleted={completedSlides.has('journey')}
                      />
                    )}

                    {currentSlideId === 'impact' && (
                      <ImpactSlide
                        isCompleted={completedSlides.has('impact')}
                      />
                    )}

                    {currentSlideId === 'style' && (
                      <StyleSlide isCompleted={completedSlides.has('style')} />
                    )}

                    {currentSlideId === 'wins' && (
                      <WinsSlide isCompleted={completedSlides.has('wins')} />
                    )}

                    {currentSlideId === 'closing' && (
                      <ClosingSlide
                        isCompleted={completedSlides.has('closing')}
                        onFadeMusic={fadeMusic}
                        onComplete={markSlideComplete}
                      />
                    )}
                  </>
                )}
              </div>

              {isError && (
                <Banner
                  className={styles.errorBanner}
                  variant="warning"
                  headline="Rewind Unavailable"
                  description={
                    <span>
                      We&apos;re experiencing some issues loading your Rewind
                      right now. Please come back later and try again.
                    </span>
                  }
                  actionSlot={
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        hide();
                      }}
                    >
                      Close
                    </Button>
                  }
                />
              )}

              {!isError && (
                <RewindControls
                  isVisible={isHovered}
                  isMuted={isMuted}
                  isPaused={isPaused}
                  isFirstSlide={isFirstSlide}
                  isLastSlide={isLastSlide}
                  onToggleMute={toggleMute}
                  onTogglePause={togglePause}
                  onPrevious={handlePreviousSlide}
                  onNext={handleNextSlide}
                  slideContainerRef={slideContainerRef}
                  currentSlideId={currentSlideId}
                  onCopyImage={captureAndCopyToClipboard}
                  onSaveImage={captureAndSave}
                  isCopying={isCopying}
                  isSaving={isSaving}
                />
              )}
            </div>
          </RewindProvider>
        </ModalBody>
      </Modal>
    );
  },
);
